import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewService } from '@/services/review.service';
import { Review } from '@bookstore/shared';
import { toast } from 'sonner';

/**
 * Hook: Get approved reviews for a product
 */
export const useProductReviews = (productId: string | undefined) => {
  return useQuery<Review[]>({
    queryKey: ['product-reviews', productId],
    queryFn: () => reviewService.getProductReviews(productId!),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 min cache
  });
};

/**
 *  HOOK: Get current user's review for a book
 * @returns 
 * 
 */
export const useUserReview = (productId: string | undefined, userId: string | undefined) => {
  return useQuery<Review | null>({
    queryKey: ['user-review', productId, userId],
    queryFn: async () => {
      const reviews = await reviewService.getUserReview(productId!, userId!);
      return reviews ;
    },
    enabled: !!productId && !!userId,
    staleTime: 5 * 60 * 1000, // 5 min cache
  });
};


/**
 * Hook: Add a new review
 */
export const useAddReview = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (review: Omit<Review, 'id' | 'createdAt' | 'updatedAt' | 'isApproved'>) =>
      reviewService.addReview(review),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['product-reviews', variables.productId] });
      qc.invalidateQueries({ queryKey: ['user-review', variables.productId] });
      
      // Invalidate product data to refresh rating/reviewCount
      qc.invalidateQueries({ queryKey: ['product', variables.productId] });
      qc.invalidateQueries({ queryKey: ['products'] });
      toast.success('Review submitted successfully!');
    },
    onError: (err: any) => toast.error(err?.message || 'Failed to submit review'),
  });
};

/**
 * Hook: Edit existing review
 */
export const useEditReview = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reviewId, data }: { reviewId: string; data: Partial<Review> }) =>
      reviewService.editReview(reviewId, data),
    onSuccess: (_, { data }) => {
      qc.invalidateQueries({ queryKey: ['product-reviews', data.productId] });
      qc.invalidateQueries({ queryKey: ['user-review', data.productId] });
      
      //Invalidate product data
      qc.invalidateQueries({ queryKey: ['product', data.productId] });
      qc.invalidateQueries({ queryKey: ['products'] });
      
      toast.success('Review updated successfully!');
    },
    onError: (err: any) => toast.error(err?.message || 'Failed to update review'),
  });
};

/**
 * Hook: Delete review
 */
export const useDeleteReview = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn:  ({ reviewId, productId }: { reviewId: string; productId: string }) => {
      return reviewService.deleteReview(reviewId);
    },
    onSuccess: (_, { productId }) => {
      // Invalidate reviews
      qc.invalidateQueries({ queryKey: ['product-reviews', productId] });
      qc.invalidateQueries({ queryKey: ['user-review', productId] });
      
      // Invalidate product data
      qc.invalidateQueries({ queryKey: ['product', productId] });
      qc.invalidateQueries({ queryKey: ['products'] });
      
      toast.success('Review deleted successfully');
    },
    onError: (err: any) => toast.error(err?.message || 'Failed to delete review'),
  });
};
